/**
 * Loop-prevention primitive for Immersive JWT re-exchange (IM-1991).
 *
 * The re-exchange path (Auth0 silent token -> POST /public/auth0_authenticate)
 * must never be hammered: not by concurrent callers, and not in a tight retry
 * loop after a genuine failure. This guard enforces both:
 *
 *   - dedupe: while an exchange is in flight, every caller gets the SAME promise.
 *   - backoff: after an exchange resolves to 'no-session' (or throws), further
 *     runs short-circuit to 'no-session' for `backoffMs` instead of re-firing.
 *
 * A successful 'ok' result clears the backoff so a later, legitimate refresh is
 * not blocked.
 *
 * This module is intentionally React-free so the guarantees are unit-testable.
 */

export type ReauthOutcome = 'ok' | 'no-session';

export type ReauthExchange = () => Promise<ReauthOutcome>;

export interface ReauthGuardOptions {
  backoffMs: number;
  /** Injectable clock for tests. Defaults to Date.now. */
  now?: () => number;
}

export interface ReauthGuard {
  run: (exchange: ReauthExchange) => Promise<ReauthOutcome>;
}

export const createReauthGuard = ({ backoffMs, now = Date.now }: ReauthGuardOptions): ReauthGuard => {
  let inFlight: Promise<ReauthOutcome> | null = null;
  let lastFailedAt = 0;

  const run = (exchange: ReauthExchange): Promise<ReauthOutcome> => {
    if (inFlight) {
      return inFlight;
    }

    if (lastFailedAt !== 0 && now() - lastFailedAt < backoffMs) {
      // Recently failed — do not re-fire. Caller treats this as "send to Auth0".
      return Promise.resolve('no-session');
    }

    const attempt = (async (): Promise<ReauthOutcome> => {
      try {
        const outcome = await exchange();
        if (outcome === 'ok') {
          lastFailedAt = 0;
        } else {
          lastFailedAt = now();
        }
        return outcome;
      } catch {
        lastFailedAt = now();
        return 'no-session';
      } finally {
        inFlight = null;
      }
    })();

    inFlight = attempt;
    return attempt;
  };

  return { run };
};
